// AUDIO THAT STARTS BY ITSELF, ON A PHONE.
//
// Khari, 2026-10-28: *"while on mobile browser, the music from the plaza does
// not play. the radio fades out, but the plaza remains quiet."*
//
// iOS Safari lets an <audio> element begin only if its FIRST play() happens
// inside a tap. After that the same element may play from anywhere — which is
// how the station works (audioEngine.unlock blesses its one element on Enter).
// The plaza stage made a new Audio() for every song from the frame loop, long
// after any tap: iOS refused each play(), the catch swallowed it, and there was
// silence with no error. (The radio still faded: holding it quiet just pauses
// its own element.)
//
// So everything in the city that starts by itself — the stage's songs and the
// DJ's heads-up, the street's voices, a story's score — BORROWS an element from
// here instead of making one: blessed on a tap by a silent play/pause, given
// its real song when borrowed (the blessing survives a new src), handed back
// when done. Every tap tops the pool back up. Desktop browsers never needed any
// of this; they get the same elements and behave exactly as before.
//
// Listeners a borrower adds through `onAudio` are removed when the element is
// handed back, so one borrower's 'ended' can never fire for the next.

import { SILENT_WAV } from './silentClip';
import { atmosTake, atmosOwns, atmosGive, readableUrl } from './atmosphere';
// The stage holds three at once (this song, the next one loading, the DJ); the
// street a few voices and a score.
// 16, not 8 (2026-10-29): at the plaza the street's voices, the street music and
// the stage borrow at once, and an empty pool hands out a plain element iOS will
// not play — the concert stayed silent on an iPhone.
const SIZE = 16;

const free = [];                 // blessed and idle
const blessed = new WeakSet();   // every element a tap has blessed
let blessing = 0;                // plays in flight
const owners = new WeakMap();    // element → AbortController for its borrower's listeners

/**
 * Bless elements until the pool is full. Must run INSIDE a user gesture: the
 * play() calls are made synchronously, before anything is awaited.
 */
export function blessAudioPool() {
  if (typeof Audio === 'undefined') return;
  while (free.length + blessing < SIZE) {
    const el = new Audio();
    el.preload = 'auto';
    el.src = SILENT_WAV;
    blessing++;
    let p = null;
    try { p = el.play(); } catch { p = null; }
    const done = (ok) => {
      blessing--;
      try { el.pause(); } catch { /* ignore */ }
      if (ok) { blessed.add(el); free.push(el); }
    };
    /* ⚠ WHAT BLESSES IS THE CALL, NOT THE PLAYBACK (2026-10-30). WebKit unlocks an
       element when play() is invoked during a user gesture, whether or not that
       particular clip then plays. So a rejection still counts, unless it is
       NotAllowedError (not a gesture after all); and a play that never settles
       (seen on iOS) must not hold `blessing` up forever, which stopped the pool
       ever topping up again. */
    if (p && typeof p.then === 'function') {
      let settled = false;
      const settle = (ok) => { if (settled) return; settled = true; done(ok); };
      p.then(() => settle(true), (err) => settle(err?.name !== 'NotAllowedError'));
      setTimeout(() => settle(true), 1500);
    } else done(true);
  }
}

/**
 * An element for `url`, blessed when the pool has one (a plain new one when it
 * is empty — that still plays everywhere but iOS, exactly as before).
 */
export const poolStats = { lent: 0, plain: 0 };   // `plain`: handed out unblessed (silent on iOS) — audio debug
export function takeAudio(url) {
  let el = free.pop();
  if (!el) { el = new Audio(); poolStats.plain += 1; if (import.meta.env.DEV) console.warn('[audio] pool empty: an unblessed player (silent on iOS)'); }
  poolStats.lent += 1;
  owners.set(el, new AbortController());
  el.preload = 'auto';
  el.loop = false;
  el.muted = false;
  try { el.volume = 1; } catch { /* read-only on iOS */ }
  el.src = url;
  return el;
}

/**
 * MUSIC, HEARD THROUGH ITS PLACE (services/atmosphere, 2026-11-05): a player
 * routed through `space` ('plaza', 'court', 'car', 'store', or a room's) when
 * that is safe — a readable bucket, not iOS, the context running — else an
 * ordinary one from the pool, exactly as `takeAudio`. Handed back the same way.
 */
export function takeMusic(url, space) {
  const el = atmosTake(url, space);
  if (!el) return takeAudio(url);
  poolStats.lent += 1;
  owners.set(el, new AbortController());
  blessed.add(el);   // never on iOS (atmosphere says no there), so it needs no tap: not "unblessed" in ?audiodebug
  return el;
}

/* ── DOWNLOADED AHEAD (2026-11-05) ────────────────────────────────────────
   Khari, on an iPhone: the first line of a conversation was heard, "Once it hit
   the second statement, it went mute." iOS does not download a clip ahead of
   play() (preload and load() are hints it ignores), so each line only started
   fetching on its moment, began late, was joined seeked past its words. A
   clip from a bucket that lets the World read it (atmosphere readableUrl) is
   fetched whole the moment it is known, and plays from memory (a blob: URL).
   Anywhere it cannot be, the player keeps the file's own URL, as before. */
const ahead = new Map();   // url → { p: Promise<string|null>, blob }
const AHEAD_MAX = 40;
export const aheadStats = { fetched: 0, failed: 0 };
/** The clip in memory, as a blob: URL (null when it cannot be: the caller keeps the URL). */
export function fetchAhead(url) {
  if (!url || typeof fetch === 'undefined' || !readableUrl(url)) return Promise.resolve(null);
  let e = ahead.get(url);
  if (e) return e.p;
  e = { blob: null };
  e.p = fetch(url, { mode: 'cors', credentials: 'omit' })
    .then((r) => (r.ok ? r.blob() : null))
    .then((b) => {
      if (!b || ahead.get(url) !== e) return null;
      e.blob = URL.createObjectURL(b.type ? b : new Blob([b], { type: 'audio/mpeg' }));
      aheadStats.fetched += 1;
      return e.blob;
    })
    .catch(() => { aheadStats.failed += 1; return null; });
  ahead.set(url, e);
  while (ahead.size > AHEAD_MAX) dropAhead(ahead.keys().next().value);
  return e.p;
}
/** Done with it: the memory is let go. */
export function dropAhead(url) {
  const e = ahead.get(url);
  if (!e) return;
  ahead.delete(url);
  if (e.blob) URL.revokeObjectURL(e.blob);
}

/** addEventListener, removed again when the element is handed back. */
export function onAudio(el, type, fn, opts = {}) {
  const ctrl = owners.get(el);
  el.addEventListener(type, fn, ctrl ? { ...opts, signal: ctrl.signal } : opts);
}

/** Stop it, drop its listeners and its download, and keep it for the next borrower. */
export function releaseAudio(el) {
  if (!el) return;
  poolStats.lent = Math.max(0, poolStats.lent - 1);
  try { owners.get(el)?.abort(); } catch { /* ignore */ }
  owners.delete(el);
  try { el.pause(); } catch { /* ignore */ }
  // A routed player goes back to its own pool (it is routed for life).
  if (atmosOwns(el)) { atmosGive(el); return; }
  // Back to the silent clip: stops the song downloading, and stays blessed.
  try { el.src = SILENT_WAV; } catch { /* ignore */ }
  // Only a blessed one goes back: a plain one made while the pool was empty
  // would pass for blessed and fail the next borrower on iOS.
  if (blessed.has(el) && free.length < SIZE * 2) free.push(el);
}

// Every tap tops the pool up (a no-op once it is full). The activation events a
// browser counts as a user gesture — on iOS a touch ends it, not starts it.
if (typeof window !== 'undefined') {
  for (const type of ['touchend', 'pointerup', 'click', 'keydown']) {
    window.addEventListener(type, blessAudioPool, { capture: true, passive: true });
  }
}

/** How many blessed players are idle right now (audio debug). */
export function poolFree() { return free.length; }

/* ⚠ A REFUSED START IS TRIED AGAIN, EVERYWHERE (2026-11-05). On an iPhone a
   pooled player's one play() can be refused with the pool healthy, and the
   .catch hid it. The plaza found it first (2026-10-30: CityStage retried every
   2 s and "now it is working"), but the street's conversations, the story scenes,
   the street music and the salon each kept their single play(): Khari, "using
   proximity does not work, nor when I 'step into' the conversation. I can see
   them talking, and I can read the captions but there is no speech audio."
   This is that retry, once, for every player started from a loop. */
const RETRY_MS = 2000;
const sounding = new Map();   // label → Map(el → record), what should be heard right now (audio debug)

/**
 * Keep a pooled player sounding. Call it on every tick it should be heard: the
 * first call starts it, and while it then sits paused (not ended) it is asked
 * again every RETRY_MS. `p` is the caller's record ({ el, … }); this keeps
 * `started`, `tried`, `retries` and `playErr` on it.
 *   at     where it should be now, in seconds, as a function (read again when
 *          its metadata arrives, and on each retry) or a number; null = from
 *          wherever it is
 *   label  how ?audiodebug groups it ('talk', 'stage', …)
 */
export function keepSounding(p, { at = null, label = 'other' } = {}) {
  const el = p?.el;
  if (!el) return;
  const now = performance.now();
  const want = () => { const s = typeof at === 'function' ? at() : at; return Number.isFinite(s) ? Math.max(0, s) : null; };
  const ok = () => { p.playErr = null; };
  const refused = (err) => { p.playErr = err?.name || String(err); };
  if (!p.started) {
    p.started = true;
    p.tried = now;
    const s = want();
    if (s != null && s > 0.3) { try { el.currentTime = s; } catch { /* seeks on metadata */ } }
    if (typeof at === 'function') onAudio(el, 'loadedmetadata', () => { const s2 = want(); if (s2 != null && s2 > 0.3) { try { el.currentTime = s2; } catch { /* ignore */ } } }, { once: true });
    el.play().then(ok, refused);
  } else if (el.paused && !el.ended && now - (p.tried || 0) > RETRY_MS) {
    p.tried = now;
    p.retries = (p.retries || 0) + 1;
    const s = want();
    if (s != null && el.readyState >= 1 && Math.abs(el.currentTime - s) > 0.3) { try { el.currentTime = s; } catch { /* ignore */ } }
    el.play().then(ok, refused);
  }
  if (!sounding.has(label)) sounding.set(label, new Map());
  sounding.get(label).set(el, { p, at: now });
}

/** For ?audiodebug: each label's players asked to sound in the last second, and how they are doing. */
export function soundingStats() {
  const now = performance.now();
  const out = [];
  for (const [label, m] of sounding) {
    let n = 0; let playing = 0; let paused = 0; let unblessed = 0; let retries = 0; let loading = 0; const errs = new Map();
    for (const [el, r] of m) {
      if (now - r.at > 1000) { m.delete(el); continue; }
      n += 1;
      if (!el.paused) playing += 1; else paused += 1;
      if (!blessed.has(el)) unblessed += 1;
      if (el.readyState < 2) loading += 1;
      retries += r.p.retries || 0;
      const e = r.p.playErr || (el.error ? `media${el.error.code}` : null);
      if (e) errs.set(e, (errs.get(e) || 0) + 1);
    }
    if (n) out.push({ label, n, playing, paused, unblessed, loading, retries, errs: [...errs].map(([k, v]) => `${k}×${v}`).join(' ') });
  }
  return out;
}
